import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight, Brain, Zap, Target, TrendingUp, Building2, Coins,
  Instagram, Youtube, Twitter, Layers, BarChart3, CalendarDays,
  Check, ChevronRight, Globe, Users, Sparkles, Shield
} from 'lucide-react';

export default function PitchPage() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';

  const sections = lang === 'tr' ? {
    problem: {
      title: 'Problem',
      points: [
        'İşletmelerin %73\'ü ne paylaşacağını bilmiyor',
        'Ajanslar pahalı: aylık $2,000 - $15,000+',
        'İçerik üretimi ortalama 4-6 saat/hafta sürüyor',
        'Çoklu kanal yönetimi karmaşık ve zaman alıcı',
      ]
    },
    solution: {
      title: 'Çözüm: TekCep',
      subtitle: 'AI destekli içerik, zamanlama ve pazarlama strateji asistanı',
      points: [
        'AI ne paylaşacağını, ne zaman paylaşacağını ve neden paylaşacağını bilir',
        '8 kanal tek platformdan yönetilir',
        'İçerik üretimi dakikaya düşer',
        'Ajans maliyetinin %90\'ı tasarruf',
      ]
    },
    product: {
      title: 'Ürün',
      features: [
        { icon: Brain, label: 'AI İçerik Motoru', desc: 'Kanal bazlı metin + görsel otomatik eşleştirme' },
        { icon: CalendarDays, label: 'AI Haftalık Plan', desc: 'Hedef bazlı otomatik içerik takvimi' },
        { icon: BarChart3, label: 'Performans Tahmini', desc: 'Yayın öncesi etkileşim ve erişim tahmini' },
        { icon: Layers, label: 'Çoklu Kanal', desc: 'Instagram, YouTube, X, Facebook, WhatsApp, Telegram, SMS, E-posta' },
        { icon: Building2, label: 'Ajans Modu', desc: 'Tek hesapla çoklu marka yönetimi' },
        { icon: Shield, label: 'Gerçek API', desc: 'Simülasyon değil, gerçek platform entegrasyonları' },
      ]
    },
    differentiator: {
      title: 'Fark',
      comparisons: [
        { label: 'Geleneksel Araçlar', items: ['Sadece paylaşım', 'Manuel içerik', 'Zamanlama yok', 'Tek kanal odaklı'], bad: true },
        { label: 'TekCep', items: ['AI karar veren strateji', 'Otomatik içerik üretimi', 'AI zamanlama + tahmin', 'Ajans & enterprise uyumlu'], bad: false },
      ]
    },
    revenue: {
      title: 'Gelir Modeli',
      models: [
        { icon: Coins, label: 'Kredi Bazlı', desc: 'Her AI aksiyonu kredi harcar, kullan kadar öde' },
        { icon: TrendingUp, label: 'Paket Bazlı', desc: 'Starter $29 / Pro $79 / Agency $199 aylık' },
        { icon: Building2, label: 'Enterprise', desc: 'Özel fiyatlama, dedike destek, SLA' },
      ]
    },
    cta: {
      title: 'Hemen Deneyin',
      subtitle: 'Kurulum 5-10 dakika · Teknik bilgi gerekmez · Ücretsiz deneme kredisi',
    }
  } : {
    problem: {
      title: 'Problem',
      points: [
        '73% of businesses don\'t know what to post',
        'Agencies are expensive: $2,000 - $15,000+/month',
        'Content creation takes 4-6 hours/week on average',
        'Multi-channel management is complex and time-consuming',
      ]
    },
    solution: {
      title: 'Solution: TekCep',
      subtitle: 'AI-powered content, timing and marketing strategy assistant',
      points: [
        'AI knows what to post, when to post, and why to post',
        '8 channels managed from one platform',
        'Content creation reduced to minutes',
        '90% cost savings vs agency fees',
      ]
    },
    product: {
      title: 'Product',
      features: [
        { icon: Brain, label: 'AI Content Engine', desc: 'Channel-based text + image auto-matching' },
        { icon: CalendarDays, label: 'AI Weekly Plan', desc: 'Goal-based automatic content calendar' },
        { icon: BarChart3, label: 'Performance Prediction', desc: 'Pre-publish engagement and reach prediction' },
        { icon: Layers, label: 'Multi-Channel', desc: 'Instagram, YouTube, X, Facebook, WhatsApp, Telegram, SMS, Email' },
        { icon: Building2, label: 'Agency Mode', desc: 'Multi-brand management from one account' },
        { icon: Shield, label: 'Real APIs', desc: 'Not simulations, real platform integrations' },
      ]
    },
    differentiator: {
      title: 'Differentiator',
      comparisons: [
        { label: 'Traditional Tools', items: ['Just publishing', 'Manual content', 'No timing', 'Single channel focus'], bad: true },
        { label: 'TekCep', items: ['AI-driven strategy', 'Auto content generation', 'AI timing + prediction', 'Agency & enterprise ready'], bad: false },
      ]
    },
    revenue: {
      title: 'Revenue Model',
      models: [
        { icon: Coins, label: 'Credit-Based', desc: 'Every AI action uses credits, pay as you go' },
        { icon: TrendingUp, label: 'Subscription', desc: 'Starter $29 / Pro $79 / Agency $199 monthly' },
        { icon: Building2, label: 'Enterprise', desc: 'Custom pricing, dedicated support, SLA' },
      ]
    },
    cta: {
      title: 'Try It Now',
      subtitle: 'Setup 5-10 minutes · No technical knowledge required · Free trial credits',
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[hsl(var(--background))]/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[hsl(46,79%,56%)] to-[hsl(174,72%,38%)] flex items-center justify-center font-bold text-black text-xs">T</div>
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>TekCep</span>
            <Badge variant="outline" className="border-white/20 text-[10px] ml-2">Pitch Deck</Badge>
          </button>
          <Button onClick={() => navigate('/auth')} className="gold-sheen bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm" data-testid="pitch-cta">
            {lang === 'tr' ? 'Hemen Başla' : 'Get Started'} <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pt-24 pb-16 space-y-16">
        {/* Hero */}
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[hsl(46,79%,56%)] to-[hsl(174,72%,38%)] flex items-center justify-center mx-auto mb-6">
            <span className="text-2xl font-bold text-black" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>T</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            TekCep
          </h1>
          <p className="text-lg text-[hsl(var(--muted-foreground))]">
            {lang === 'tr' ? 'AI Destekli Pazarlama İşletim Sistemi' : 'AI-Powered Marketing Operating System'}
          </p>
        </div>

        {/* Problem */}
        <section>
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <Target size={24} className="text-red-400" /> {sections.problem.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sections.problem.points.map((p, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-red-500/5 border border-red-500/15">
                <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-red-300">{i + 1}</span>
                </div>
                <p className="text-sm">{p}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="divider-sheen" />

        {/* Solution */}
        <section>
          <h2 className="text-2xl font-semibold mb-2 flex items-center gap-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <Zap size={24} className="text-[hsl(var(--primary))]" /> {sections.solution.title}
          </h2>
          <p className="text-[hsl(var(--muted-foreground))] mb-6">{sections.solution.subtitle}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sections.solution.points.map((p, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-[hsl(var(--primary))]/5 border border-[hsl(var(--primary))]/15">
                <Check size={18} className="text-[hsl(var(--primary))] shrink-0 mt-0.5" />
                <p className="text-sm">{p}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="divider-sheen" />

        {/* Product */}
        <section>
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <Sparkles size={24} className="text-[hsl(var(--accent))]" /> {sections.product.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sections.product.features.map((f, i) => (
              <Card key={i} className="card-sheen rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
                <CardContent className="p-5">
                  <f.icon size={24} className="text-[hsl(var(--accent))] mb-3" />
                  <h3 className="font-semibold mb-1">{f.label}</h3>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <div className="divider-sheen" />

        {/* Differentiator */}
        <section>
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <TrendingUp size={24} className="text-[hsl(var(--primary))]" /> {sections.differentiator.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sections.differentiator.comparisons.map((comp, ci) => (
              <Card key={ci} className={`rounded-xl border bg-[hsl(var(--card))]/80 ${comp.bad ? 'border-red-500/20' : 'border-emerald-500/20'}`}>
                <CardContent className="p-5 space-y-3">
                  <h3 className={`font-semibold ${comp.bad ? 'text-red-300' : 'text-emerald-300'}`}>{comp.label}</h3>
                  {comp.items.map((item, ii) => (
                    <div key={ii} className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                      {comp.bad ? <div className="w-1.5 h-1.5 rounded-full bg-red-500/50" /> : <Check size={14} className="text-emerald-400" />}
                      {item}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <div className="divider-sheen" />

        {/* Revenue Model */}
        <section>
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <Coins size={24} className="text-[hsl(var(--primary))]" /> {sections.revenue.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sections.revenue.models.map((m, i) => (
              <Card key={i} className="card-sheen rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
                <CardContent className="p-5">
                  <m.icon size={24} className="text-[hsl(var(--primary))] mb-3" />
                  <h3 className="font-semibold mb-1">{m.label}</h3>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">{m.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <div className="divider-sheen" />

        {/* CTA */}
        <section className="text-center py-8">
          <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {sections.cta.title}
          </h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-8">{sections.cta.subtitle}</p>
          <Button size="lg" onClick={() => navigate('/auth')}
            className="gold-sheen bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105 px-10 text-lg shadow-[0_10px_30px_-12px_rgba(212,175,55,0.35)]">
            {lang === 'tr' ? 'Hemen Başla' : 'Get Started'} <ArrowRight size={20} className="ml-2" />
          </Button>
        </section>
      </div>
    </div>
  );
}
